// Authentication context.
//
// Single source of truth for the current JWT. Loads from localStorage on
// mount; refreshes the axios Authorization header whenever the token
// changes; exposes signin/signout/setToken to consumers.
//
// Routing rules consumed by ProtectedRoute:
//   - no token             → /signin
//   - scope=password_change → /change-password
//   - role-bearing token   → render children

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setAuthToken } from "../lib/api";
import { decode, isExpired } from "../lib/jwt";
import type { DecodedJwt } from "../lib/types";

const STORAGE_KEY = "paymentproducts.team.jwt";

interface AuthState {
  token: string | null;
  decoded: DecodedJwt | null;
  /** True when AuthProvider is still resolving the initial token from storage. */
  initializing: boolean;
}

interface AuthContextValue extends AuthState {
  /** Persist a new token (e.g., after signin or password change). */
  setToken: (token: string | null) => void;
  /** Clear token + axios header. */
  signout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    token: null,
    decoded: null,
    initializing: true,
  });

  // Initial token load from localStorage.
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const decoded = decode(stored);
    if (!stored || !decoded || isExpired(decoded)) {
      // Token missing, malformed, or expired — clear and start fresh.
      localStorage.removeItem(STORAGE_KEY);
      setAuthToken(null);
      setState({ token: null, decoded: null, initializing: false });
      return;
    }
    setAuthToken(stored);
    setState({ token: stored, decoded, initializing: false });
  }, []);

  const setToken = useCallback((token: string | null) => {
    if (!token) {
      localStorage.removeItem(STORAGE_KEY);
      setAuthToken(null);
      setState({ token: null, decoded: null, initializing: false });
      return;
    }
    const decoded = decode(token);
    if (!decoded) {
      // Defensive: malformed tokens should never happen but if they do,
      // treat as signout to keep the UI in a known state.
      localStorage.removeItem(STORAGE_KEY);
      setAuthToken(null);
      setState({ token: null, decoded: null, initializing: false });
      return;
    }
    localStorage.setItem(STORAGE_KEY, token);
    setAuthToken(token);
    setState({ token, decoded, initializing: false });
  }, []);

  const signout = useCallback(() => setToken(null), [setToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, setToken, signout }),
    [state, setToken, signout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
